import { useState, useMemo, useEffect } from "react";
import { Filter, Search } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "../../../components/dashboard-layout/DashboardLayout";
import styles from "../../../styles/__labReportManagement.module.scss";
import logo from "../../../assets/images/logo.png";
import user from "../../../assets/images/user.png";
import { IoMdSettings } from "react-icons/io";
import DialogReschedule from "../../../components/dialog-reschedule/DialogReschedule";
import DialogCreateFollowUp from "../../../components/dialog-create/DialogCreateFollowUp";
import DialogManageAvailability from "../../../components/dialog-manage-availability/DialogManageAvailability";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchDoctorAppointmentsReducer } from "../../../reducer/appointments/fetchDoctorAppointmentsReducer";
import { joinDoctorAppointmentReducer } from "../../../reducer/joinDoctorAppointment/joinDoctorAppointmentReducer";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import { toast } from "../../../components/toast";

import { FiDownload } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import {
  getLabReports,
  resendLabReport,
} from "../../../services/labReportsService";

const LabReportManagement = ({ onLogout, onNavigateToDashboard }) => {
  const { t, i18n } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const itemsPerPage = 10;
  // Search typeahead dropdown state
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();
  const [date, setDate] = useState(null);
  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openDialogCreateFollowUp, setOpenDialogCreateFollowUp] =
    useState(false);
  const [openDialogManageAvailability, setOpenDialogManageAvailability] =
    useState(false);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);

  // Lab reports state
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const dispatch = useDispatch();
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  // Fetch lab reports with current filters
  const fetchReportsWithFilters = async (
    pageNum = currentPage,
    statusOverride = undefined,
    dateOverride = undefined,
    searchOverride = undefined,
  ) => {
    try {
      setLoading(true);

      // Use override values if provided, otherwise use current state
      const dateToUse = dateOverride !== undefined ? dateOverride : date;
      const locationToUse =
        statusOverride !== undefined ? statusOverride : locationFilter;
      const searchToUse =
        searchOverride !== undefined ? searchOverride : searchTerm;

      const formattedDate = formatDate(dateToUse);

      // Build params object
      const params = {
        page: pageNum,
        limit: itemsPerPage,
      };

      // Only add filters if they have values
      if (
        formattedDate &&
        formattedDate !== null &&
        formattedDate !== "null" &&
        formattedDate !== ""
      ) {
        params.date = formattedDate;
      }

      if (searchToUse && searchToUse.trim()) {
        params.search = searchToUse.trim();
      }

      if (locationToUse) {
        params.location = locationToUse; // HOME_VISIT / ON_SITE
      }

      console.log("Fetching reports with params:", params);

      const response = await getLabReports(params);

      if (response && response.status) {
        setReports(response.data.reports || []);
        setPagination(
          response.data.pagination || {
            page: pageNum,
            limit: itemsPerPage,
            total: 0,
            totalPages: 1,
          },
        );
        setTotalPages(response.data.pagination?.totalPages || 1);
        setCurrentPage(pageNum);

        console.log(
          "Reports loaded successfully:",
          response.data.reports?.length || 0,
          "reports",
        );
      } else {
        console.error("Failed to fetch reports:", response);
        toast.error(response?.message || t("lab.reportManagement.fetchError"));
        setReports([]);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error(t("lab.reportManagement.fetchErrorTryAgain"));
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle resend report
  const handleResendReport = async (reportId, reportName) => {
    try {
      const confirmed = window.confirm(
        t("lab.reportManagement.confirmResend", { name: reportName }),
      );

      if (!confirmed) {
        return;
      }

      toast.info(t("lab.reportManagement.resendingReport"));

      const response = await resendLabReport(reportId);

      if (response && response.status) {
        toast.success(t("lab.reportManagement.resendSuccess"));
        // Refresh the reports list to update the status
        await fetchReportsWithFilters();
      } else {
        console.error("Failed to resend report:", response);
        toast.error(response?.message || t("lab.reportManagement.resendError"));
      }
    } catch (error) {
      console.error("Error resending report:", error);
      toast.error(t("lab.reportManagement.resendErrorTryAgain"));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return null;
    try {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Format status text for display
  const formatStatusText = (status) => {
    if (!status) return "";
    // Convert to display format
    const statusDisplayMap = {
      pending: "Pending",
      upcoming: "Upcoming",
      ongoing: "Ongoing",
      completed: "Completed",
      cancelled: "Cancelled",
      reschedule_requested: "Reschedule Requested",
      "reschedule-requested": "Reschedule Requested",
    };

    const lowerStatus = status.toLowerCase();
    if (statusDisplayMap[lowerStatus]) {
      return statusDisplayMap[lowerStatus];
    }

    // Capitalize first letter of each word for other statuses
    return status
      .split(/[_\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Normalize status value for API (convert display format to API format)
  const normalizeStatusForAPI = (status) => {
    if (!status || status === "All") return null;
    // Convert display format to API format for report status
    const statusMap = {
      "Not Sent": "not_sent",
      Sent: "sent",
      not_sent: "not_sent",
      sent: "sent",
      NOT_SENT: "not_sent",
      SENT: "sent",
    };
    return statusMap[status] || status.toLowerCase().replace(/\s+/g, "_");
  };

  // Function to fetch appointments with current filters
  // Optional parameters allow overriding filter values (useful for tab clicks and reset)
  // Use undefined as default to distinguish between "not provided" and "explicitly null"
  const fetchAppointmentsWithFilters = fetchReportsWithFilters; // Alias for compatibility

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchReportsWithFilters(page);
  };

  const resetFilters = () => {
    // Reset state values first
    setSearchTerm("");
    setLocationFilter("");
    setDate(null);
    setSearchResults([]);
    setShowSearchDropdown(false);
    setCurrentPage(1);

    // Immediately fetch with reset values
    fetchReportsWithFilters(1, "", null, "");
  };

  // Debounced search typeahead — matches patient name OR booking number (name-aware
  // at the gateway); results shown in a dropdown, clicking navigates to the detail.
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      return;
    }
    const handler = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const res = await getLabReports({ search: term, limit: 10 });
        setSearchResults(res?.data?.reports || []);
        setShowSearchDropdown(true);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  const joinAppointment = async (id) => {
    try {
      const result = await dispatch(
        joinDoctorAppointmentReducer({ appointmentId: id }),
      ).unwrap();
      console.log("result:", result);
      // reducer returns response.data.data (normalized) so join_url should be on result
      const joinUrl = result?.join_url || result?.data?.join_url;
      if (joinUrl) {
        // open in a new tab
        window.open(joinUrl, "_blank");
      } else {
        console.warn(
          "No join_url received from joinDoctorAppointmentReducer",
          result,
        );
      }
    } catch (err) {
      console.error("Failed to join appointment:", err);
    }
  };

  // Initial load with default filters
  useEffect(() => {
    fetchReportsWithFilters(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange1 = (data) => {
    const nextPage = data + 1;
    fetchReportsWithFilters(nextPage);
  };

  const handlePageChange11 = (data) => {
    const prevPage = data - 1;
    if (prevPage >= 1) {
      fetchReportsWithFilters(prevPage);
    }
  };
  const handleBackDetail = (state, data) => {
    setShowDetails(state);
    setSelectedAppointment(data);
  };

  const handleReschedules = (data) => {
    setOpenDialogReschedule(true);
    setSelectedAppointment(data);
  };

  const CreateFollowUp = (data) => {
    setOpenDialogCreateFollowUp(true);
    setSelectedAppointment(data);
  };

  // Format report data for display
  const formatReportData = (report) => {
    return {
      id: report.bookingNumber || report.id,
      name: report.patient?.name || t("lab.reportManagement.unknownPatient"),
      ServiceDate: report.serviceDate
        ? format(new Date(report.serviceDate), "dd/MM/yyyy")
        : t("lab.reportManagement.noData"),
      ServiceTime: report.serviceTime || t("lab.reportManagement.noData"),
      MobileNumber:
        report.patient?.mobileNumber ||
        report.urgency ||
        t("lab.reportManagement.noData"),
      ReportStatus:
        report.reportStatus === "SENT"
          ? t("lab.reportManagement.status.sent")
          : t("lab.reportManagement.status.notSent"),
      rawData: report, // Keep original data for actions
    };
  };

  // Process reports for display
  const receivedTableData = reports.map(formatReportData);

  const getStatusColor = (status) => {
    // Check for translated status values
    if (status === t("lab.reportManagement.status.sent") || status === "Sent") {
      return "reportstatus-Sent";
    }
    if (
      status === t("lab.reportManagement.status.notSent") ||
      status === "Not Sent"
    ) {
      return "reportstatus-NotSent";
    }
    return "";
  };

  return (
    <LabDashboardLayout
      currentPage="lab_report-management"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <DialogReschedule
        open={openDialogReschedule}
        onClose={() => setOpenDialogReschedule(false)}
        children={undefined}
        selectedAppointment={selectedAppointment}
      />
      <DialogCreateFollowUp
        open={openDialogCreateFollowUp}
        onClose={() => {
          setOpenDialogCreateFollowUp(false);
        }}
        children={undefined}
        selectedAppointment={selectedAppointment}
      />
      <DialogManageAvailability
        open={openDialogManageAvailability}
        onClose={() => {
          setOpenDialogManageAvailability(false);
        }}
        children={undefined}
      />
      <DialogPatientRegistration
        open={openDialogPatientRegistration}
        onClose={() => {
          setOpenDialogPatientRegistration(false);
        }}
        children={undefined}
      />

      {showDetails ? (
        <AppointmentDetails
          handleBack={() => setShowDetails(false)}
          homeVisit={false}
          selectedAppointment={selectedAppointment}
        />
      ) : (
        <>
          <div className={styles["apt-page-header"]}>
            <h1>{t("lab.reportManagement.title")}</h1>
          </div>
          {/* Info banner: clarifies which orders appear on this screen. */}
          <div
            role="note"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              margin: "0 0 1.25rem 0",
              padding: "0.85rem 1.1rem",
              borderRadius: "0.75rem",
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
              color: "#1E40AF",
              fontSize: "0.9rem",
            }}
          >
            <span style={{ fontSize: "1.1rem" }}>ℹ️</span>
            <span>
              {t(
                "lab.reportManagement.infoBanner",
                "Only orders whose report/result has been uploaded but not yet sent to the patient are displayed here.",
              )}
            </span>
          </div>
          <div className={styles["apt-appointments-section"]}>
            {/* Filters */}
            <div className={styles["apt-filters"]}>
              <div className={styles["apt-filter-group"]}>
                {/* <span className={styles["apt-filter-icon-wrap"]}>
                  <Filter className={styles["apt-filter-icon"]} />
                </span> */}
                <div
                  className={styles["apt-search-wrapper"]}
                  style={{ position: "relative" }}
                >
                  <Search className={styles["apt-search-icon"]} />
                  <input
                    type="text"
                    placeholder={t(
                      "lab.reportManagement.filters.searchPlaceholder",
                    )}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => {
                      if (searchResults.length > 0) setShowSearchDropdown(true);
                    }}
                    onBlur={() =>
                      setTimeout(() => setShowSearchDropdown(false), 150)
                    }
                    className={styles["apt-search-input"]}
                  />
                  {showSearchDropdown && searchTerm.trim() && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {searchLoading ? (
                        <div
                          style={{
                            padding: "12px 14px",
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          Searching...
                        </div>
                      ) : searchResults.length === 0 ? (
                        <div
                          style={{
                            padding: "12px 14px",
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          No reports found
                        </div>
                      ) : (
                        searchResults.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setShowSearchDropdown(false);
                              navigate(
                                `/lab_appointment_detail/${item.id}?source=ordermanagement`,
                              );
                            }}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: "2px",
                              width: "100%",
                              padding: "10px 14px",
                              background: "none",
                              border: "none",
                              borderBottom: "1px solid #f3f4f6",
                              cursor: "pointer",
                              textAlign: "left",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#111827",
                              }}
                            >
                              {item.patient?.name ||
                                `Patient ${item.patientId || ""}`}
                            </span>
                            <span
                              style={{ fontSize: "12px", color: "#6b7280" }}
                            >
                              {item.bookingNumber ||
                                item.booking_number ||
                                `#${item.id}`}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <select
                  value={locationFilter}
                  onChange={(e) => {
                    setLocationFilter(e.target.value);
                    setCurrentPage(1);
                    fetchReportsWithFilters(1, e.target.value);
                  }}
                  className={styles["apt-status-filter"]}
                  style={{ paddingRight: "30px" }}
                >
                  <option value="">All Locations</option>
                  <option value="HOME_VISIT">Home Visit</option>
                  <option value="ON_SITE">On-Site</option>
                </select>

                <button
                  className={styles["apt-apply-btn"]}
                  onClick={() => {
                    setCurrentPage(1);
                    fetchReportsWithFilters(1);
                  }}
                >
                  {t("lab.reportManagement.filters.applyFilters")}
                </button>

                {/* <button className={styles["apt-apply-btn"]}>{t('appointments.filters.button.apply')}</button> */}
                {/* <button
                  className={styles["apt-apply-btn"]}
                  onClick={() => {
                    setCurrentPage(1);
                    fetchAppointmentsWithFilters(1);
                  }}
                >
                  {t("appointments.filters.button.apply")}
                </button> */}
                <button
                  className={styles["apt-reset-btn"]}
                  onClick={resetFilters}
                >
                  {t("lab.reportManagement.filters.clearSearch")}
                </button>
              </div>
            </div>

            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>{t("lab.reportManagement.table.patientName")}</th>
                    <th>{t("lab.reportManagement.table.serviceDate")}</th>
                    <th>{t("lab.reportManagement.table.serviceTime")}</th>
                    <th>{t("lab.reportManagement.table.mobileNumber")}</th>
                    <th>{t("lab.reportManagement.table.reportStatus")}</th>
                    <th>{t("lab.reportManagement.table.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6}>
                        <Loader />
                      </td>
                    </tr>
                  ) : receivedTableData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#64748b",
                        }}
                      >
                        {t(
                          "lab.reportManagement.noReportsFound",
                          "No reports found",
                        )}
                      </td>
                    </tr>
                  ) : (
                    receivedTableData.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.name}</td>
                        <td>{item.ServiceDate}</td>
                        <td>{item.ServiceTime}</td>
                        <td>{item.MobileNumber}</td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.ReportStatus)]
                            }`}
                          >
                            {item.ReportStatus}
                          </span>
                        </td>
                        {/* <td>{item.ReportStatus}</td> */}
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            {(item.ReportStatus ===
                              t("lab.reportManagement.status.sent") ||
                              item.ReportStatus === "Sent") && (
                              <Link
                                to={`/lab_Report_management_patient_appoitment_details/${item.rawData?.id || item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title={t(
                                    "lab.reportManagement.actions.viewReportDetails",
                                  )}
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>
                            )}
                            {(item.ReportStatus ===
                              t("lab.reportManagement.status.notSent") ||
                              item.ReportStatus === "Not Sent") && (
                              <button
                                className={styles["apt-view-btn"]}
                                title={t(
                                  "lab.reportManagement.actions.resendReport",
                                )}
                                onClick={() =>
                                  handleResendReport(
                                    item.rawData?.id || item.id,
                                    item.name,
                                  )
                                }
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.96008 1.66202C8.96074 1.7441 8.94523 1.8255 8.91442 1.90158C8.88362 1.97766 8.83814 2.04693 8.78056 2.10543C8.72299 2.16393 8.65446 2.21052 8.57888 2.24254C8.5033 2.27455 8.42216 2.29137 8.34008 2.29202C6.91508 2.30369 5.86508 2.35035 5.05258 2.50952C4.25675 2.66535 3.73341 2.92119 3.32925 3.32535C2.85425 3.80035 2.58175 4.44202 2.43841 5.50869C2.29341 6.59202 2.29175 8.01619 2.29175 9.99785C2.29175 11.9812 2.29341 13.4045 2.43841 14.4879C2.58175 15.5537 2.85508 16.1962 3.33008 16.6712C3.80425 17.1462 4.44675 17.4187 5.51258 17.562C6.59591 17.707 8.02008 17.7087 10.0026 17.7087C11.9851 17.7087 13.4084 17.707 14.4917 17.562C15.5584 17.4187 16.2001 17.1454 16.6751 16.6704C17.0792 16.267 17.3351 15.7437 17.4909 14.9479C17.6492 14.1345 17.6959 13.0845 17.7084 11.6604C17.7097 11.4946 17.7769 11.3361 17.895 11.2199C18.0132 11.1036 18.1727 11.039 18.3384 11.0404C18.5042 11.0417 18.6626 11.1088 18.7789 11.2269C18.8952 11.3451 18.9597 11.5046 18.9584 11.6704C18.9459 13.0912 18.9017 14.2479 18.7167 15.1887C18.5301 16.147 18.1892 16.9245 17.5584 17.5554C16.8134 18.3012 15.8626 18.6387 14.6584 18.8004C13.4817 18.9587 11.9742 18.9587 10.0501 18.9587H9.95425C8.03008 18.9587 6.52258 18.9587 5.34591 18.8004C4.14175 18.6387 3.19175 18.3004 2.44591 17.5554C1.70008 16.8095 1.36258 15.8587 1.20008 14.6554C1.04175 13.4779 1.04175 11.9704 1.04175 10.0462V9.95035C1.04175 8.02619 1.04175 6.51869 1.20008 5.34202C1.36175 4.13785 1.70008 3.18702 2.44591 2.44202C3.07591 1.81119 3.85425 1.47035 4.81258 1.28369C5.75258 1.09869 6.90925 1.05369 8.32925 1.04202C8.41139 1.04125 8.49289 1.05668 8.56906 1.08744C8.64524 1.11819 8.71461 1.16366 8.7732 1.22124C8.83179 1.27882 8.87845 1.34739 8.91052 1.42303C8.94259 1.49866 8.95943 1.57987 8.96008 1.66202ZM13.7251 1.22535C13.8423 1.10831 14.0011 1.04257 14.1667 1.04257C14.3324 1.04257 14.4912 1.10831 14.6084 1.22535L18.7751 5.39202C18.8921 5.50921 18.9579 5.66806 18.9579 5.83369C18.9579 5.99931 18.8921 6.15817 18.7751 6.27535L14.6084 10.442C14.5512 10.5034 14.4822 10.5527 14.4055 10.5868C14.3289 10.621 14.2461 10.6394 14.1622 10.6408C14.0783 10.6423 13.9949 10.6269 13.9171 10.5955C13.8393 10.564 13.7686 10.5172 13.7092 10.4579C13.6499 10.3985 13.6031 10.3278 13.5716 10.25C13.5402 10.1722 13.5248 10.0888 13.5263 10.0049C13.5277 9.921 13.5461 9.83824 13.5803 9.76157C13.6144 9.68491 13.6637 9.6159 13.7251 9.55869L16.8251 6.45869H11.6667C10.3734 6.45869 9.61091 7.08952 9.34341 7.35869L9.18341 7.51869L9.02508 7.67702C8.75591 7.94369 8.12508 8.70702 8.12508 10.0004V12.5004C8.12508 12.6661 8.05923 12.8251 7.94202 12.9423C7.82481 13.0595 7.66584 13.1254 7.50008 13.1254C7.33432 13.1254 7.17535 13.0595 7.05814 12.9423C6.94093 12.8251 6.87508 12.6661 6.87508 12.5004V10.0004C6.87508 8.26369 7.73091 7.20035 8.14258 6.79119L8.30091 6.63285L8.45758 6.47619C8.86675 6.06452 9.93008 5.20869 11.6667 5.20869H16.8251L13.7251 2.10869C13.608 1.9915 13.5423 1.83265 13.5423 1.66702C13.5423 1.50139 13.608 1.34254 13.7251 1.22535Z"
                                    fill="#1C398E"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={styles["apt-pagination"]}>
              <div className={styles["apt-pagination-info"]}>
                {t("lab.orderManagement.pagination.showing")}{" "}
                {(currentPage - 1) * itemsPerPage + 1}{" "}
                {t("lab.orderManagement.pagination.to")}{" "}
                {Math.min(currentPage * itemsPerPage, pagination.total)}{" "}
                {t("lab.orderManagement.pagination.of")} {pagination.total}
              </div>
              <div className={styles["apt-pagination-controls"]}>
                <button
                  className={`${styles["apt-pagination-btn"]} ${
                    currentPage === 1 ? styles["disabled"] : ""
                  }`}
                  // onClick={() => handlePageChange(currentPage - 1)}
                  // onClick={() => handlePageChange11(currentPage)}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t("lab.orderManagement.pagination.previous")}
                </button>
                <div className={styles["apt-page-numbers"]}>
                  {/* {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    className={`${styles["apt-page-btn"]} ${
                      styles[`${currentPage === page ? "active" : ""}`]
                    }`}
                    onClick={() => handlePageChange(currentPage)}
                  >
                    {page}
                  </button>
                );
              })} */}

                  {Array.from({ length: totalPages }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        className={`${styles["apt-page-btn"]} ${
                          styles[`${currentPage === page ? "active" : ""}`]
                        }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  className={`${styles["apt-pagination-btn"]} ${
                    styles["apt-next-btn"]
                  } ${currentPage === totalPages ? styles["disabled"] : ""}`}
                  // onClick={() => handlePageChange(currentPage + 1)}
                  // onClick={() => handlePageChange1(currentPage)}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t("lab.orderManagement.pagination.next")}
                </button>
              </div>
            </div>
          </div>{" "}
        </>
      )}
    </LabDashboardLayout>
  );
};

export default LabReportManagement;
